import { LineBuilder, unitSphere, type Rgba } from '../gfx/geometry.ts';
import { LineRenderer, type DrawCall, type GpuBatch } from '../gfx/renderer.ts';
import { OrbitCamera } from '../gfx/orbit.ts';
import { project } from '../gfx/mat4.ts';
import type { RoomModel } from '../dsp/roomModel.ts';

/**
 * The room, drawn from the measurement.
 *
 * The wavefront is not decoration. It expands at the speed of sound the
 * measurement actually used, its brightness follows the measured RT60, and it
 * reaches each shell at exactly the delay that shell was detected at. Watching
 * it cross a sphere is watching the range profile happen in space.
 */

interface Palette {
  ink: Rgba;
  faint: Rgba;
  accent: Rgba;
  clear: [number, number, number, number];
}

interface ShellLabel {
  element: HTMLElement;
  radiusM: number;
}

export interface RoomViewHandle {
  update(model: RoomModel): void;
  destroy(): void;
}

/** Seconds of wall-clock per second of simulated sound. */
const TIME_SCALE = 1 / 340;

export async function createRoomView(
  canvas: HTMLCanvasElement,
  overlay: HTMLElement,
): Promise<RoomViewHandle> {
  const renderer = await LineRenderer.create(canvas);

  let model: RoomModel | null = null;
  let statics: GpuBatch | null = null;
  let labels: ShellLabel[] = [];
  let frame = 0;
  let startedAt = performance.now();
  let onScreen = true;

  const wavefront = renderer.upload(unitSphere([1, 1, 1, 1]), 'wavefront');

  const camera = new OrbitCamera(
    canvas,
    { distance: 9, minDistance: 1.2, maxDistance: 60 },
    () => {},
  );

  const palette = (): Palette => {
    const style = getComputedStyle(document.documentElement);
    const read = (name: string, fallback: string): Rgba =>
      parseColour(style.getPropertyValue(name).trim() || fallback);
    const paper = parseColour(style.getPropertyValue('--paper').trim() || '#faf9f6');
    return {
      ink: read('--ink', '#111111'),
      faint: read('--ink-faint', 'rgba(17,17,17,0.38)'),
      accent: read('--accent', '#e3000f'),
      clear: [paper[0], paper[1], paper[2], 1],
    };
  };

  function rebuild(next: RoomModel): void {
    model = next;
    startedAt = performance.now();

    const colours = palette();
    const builder = new LineBuilder();

    // Floor grid: one line per metre, so the scene carries its own scale bar.
    builder.grid(Math.ceil(next.extentM), 1, alpha(colours.ink, 0.1), -next.cubeHalfM);

    // The cube the volume estimate literally describes.
    builder.box(next.cubeHalfM, next.cubeHalfM, next.cubeHalfM, alpha(colours.ink, 0.55));

    // The blind zone: everything the direct blast masks.
    builder.circle(next.blindRangeM, 'y', -next.cubeHalfM, alpha(colours.accent, 0.35), 96);

    // The phone.
    builder.cross(0.16, alpha(colours.ink, 0.9));

    // One sphere per detected echo: known radius, unknown bearing.
    for (const shell of next.shells) {
      const colour = shell.confident ? colours.accent : colours.faint;
      const strength = shell.confident ? 0.5 : 0.22;
      builder.sphere(shell.radiusM, alpha(colour, strength), {
        rings: shell.confident ? 7 : 4,
        meridians: shell.confident ? 14 : 8,
        segments: 72,
      });
    }

    statics?.destroy();
    statics = renderer.upload(builder.build(), 'room-statics');

    for (const label of labels) label.element.remove();
    labels = next.shells.map((shell) => {
      const element = document.createElement('span');
      element.className = `room-label${shell.confident ? '' : ' is-weak'}`;
      element.textContent = `${shell.radiusM.toFixed(2)} m`;
      element.setAttribute('aria-hidden', 'true');
      overlay.append(element);
      return { element, radiusM: shell.radiusM };
    });

    camera.reframe(next.extentM * 2.4, { min: next.extentM * 0.35, max: next.extentM * 9 }, 0);
  }

  function draw(): void {
    frame = requestAnimationFrame(draw);
    if (!model || !statics || !onScreen) return;

    const { width, height } = renderer.resize();
    const colours = palette();
    const viewProjection = camera.viewProjection(width / Math.max(1, height));

    // The wavefront: real metres per real second, slowed by a fixed factor so
    // the eye can follow it. It restarts once the room has gone quiet.
    const elapsed = (performance.now() - startedAt) / 1000;
    const soundSeconds = elapsed * TIME_SCALE;
    const cycle = Math.max(model.rt60Sec * 1.35, (2 * model.extentM) / model.speedOfSoundMs);
    const phase = soundSeconds % cycle;
    const radius = (phase * model.speedOfSoundMs) / 2;

    // Brightness follows the measured decay: -60 dB over exactly RT60.
    const decay = Math.exp((-6.907755 * phase) / Math.max(model.rt60Sec, 0.05));
    const withinScene = radius < model.extentM * 1.6 ? 1 : 0;

    const draws: DrawCall[] = [
      { batch: statics, scale: 1, alpha: 1 },
      { batch: wavefront, scale: Math.max(radius, 0.001), alpha: 0.75 * decay * withinScene },
    ];

    renderer.render(viewProjection, draws, {
      clear: colours.clear,
      fadeNear: camera.distance * 0.35,
      fadeFar: camera.distance * 2.3,
    });

    positionLabels(viewProjection, width, height);
  }

  function positionLabels(viewProjection: Float32Array, width: number, height: number): void {
    const ratio = Math.min(window.devicePixelRatio || 1, 2);
    const cssWidth = width / ratio;
    const cssHeight = height / ratio;

    for (const label of labels) {
      // Anchor at the top of each sphere, which stays clear of the floor grid.
      const point = project(viewProjection, [0, label.radiusM, 0]);
      if (point.w <= 0) {
        label.element.style.opacity = '0';
        continue;
      }
      const x = (point.x * 0.5 + 0.5) * cssWidth;
      const y = (1 - (point.y * 0.5 + 0.5)) * cssHeight;
      const visible = x > -40 && x < cssWidth + 40 && y > -20 && y < cssHeight + 20;
      label.element.style.opacity = visible ? '1' : '0';
      label.element.style.transform = `translate(${x.toFixed(1)}px, ${y.toFixed(1)}px)`;
    }
  }

  // Stop animating once the stage scrolls away. This runs on a phone that is
  // being held up mid-measurement, so a 60 Hz loop nobody can see is a real
  // cost - and the wavefront's clock is derived from elapsed time rather than
  // accumulated frames, so pausing never desynchronises it.
  const observer = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) onScreen = entry.isIntersecting;
    },
    { threshold: 0.01 },
  );
  observer.observe(canvas);

  frame = requestAnimationFrame(draw);

  return {
    update: rebuild,
    destroy(): void {
      cancelAnimationFrame(frame);
      observer.disconnect();
      camera.destroy();
      for (const label of labels) label.element.remove();
      statics?.destroy();
      wavefront.destroy();
      renderer.destroy();
    },
  };
}

function alpha(colour: Rgba, a: number): Rgba {
  return [colour[0], colour[1], colour[2], colour[3] * a];
}

/** Parse the hex and rgb/rgba forms the stylesheet actually uses. */
function parseColour(input: string): Rgba {
  const text = input.trim();
  if (text.startsWith('#')) {
    const hex = text.slice(1);
    const full =
      hex.length === 3
        ? hex.split('').map((c) => c + c).join('')
        : hex.padEnd(6, '0').slice(0, 6);
    return [
      parseInt(full.slice(0, 2), 16) / 255,
      parseInt(full.slice(2, 4), 16) / 255,
      parseInt(full.slice(4, 6), 16) / 255,
      1,
    ];
  }
  const match = text.match(/rgba?\(([^)]+)\)/);
  if (match) {
    const parts = match[1].split(/[,/\s]+/).filter(Boolean).map(Number);
    return [
      (parts[0] ?? 0) / 255,
      (parts[1] ?? 0) / 255,
      (parts[2] ?? 0) / 255,
      parts[3] === undefined ? 1 : parts[3],
    ];
  }
  return [0, 0, 0, 1];
}
